import { format, startOfMonth, endOfMonth, subMonths, eachMonthOfInterval, eachWeekOfInterval, startOfWeek, addDays } from 'date-fns';
import { toJsDate } from './dateUtils';
import { EXPENSE_CATEGORIES } from './constants';
import { getCategoryIcon } from './expenseUtils';

/**
 * Compute start/end dates from a preset key.
 */
export const getDateRangeFromPreset = (preset) => {
  const now = new Date();
  const endDate = endOfMonth(now);

  switch (preset) {
    case 'this_month':
      return { startDate: startOfMonth(now), endDate };
    case 'last_3':
      return { startDate: startOfMonth(subMonths(now, 2)), endDate };
    case 'last_6':
      return { startDate: startOfMonth(subMonths(now, 5)), endDate };
    case 'this_year':
      return { startDate: new Date(now.getFullYear(), 0, 1), endDate };
    default:
      return { startDate: startOfMonth(subMonths(now, 2)), endDate };
  }
};

/**
 * Filter expenses to a date range (inclusive).
 */
export const filterExpensesByDateRange = (expenses, startDate, endDate) => {
  return expenses.filter(expense => {
    const date = toJsDate(expense.date);
    if (!date) return false;
    return date >= startDate && date <= endDate;
  });
};

/**
 * Summary statistics for filtered expenses.
 */
export const computeSummaryStats = (filteredExpenses) => {
  if (!filteredExpenses.length) {
    return { totalSpent: 0, averageMonthly: 0, topCategory: null, expenseCount: 0 };
  }

  const totalSpent = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  // Count distinct months
  const months = new Set(
    filteredExpenses.map(e => {
      const d = toJsDate(e.date);
      return d ? format(d, 'yyyy-MM') : null;
    }).filter(Boolean)
  );
  const averageMonthly = months.size > 0 ? totalSpent / months.size : totalSpent;

  // Top category by total amount
  const categoryTotals = {};
  filteredExpenses.forEach(e => {
    const cat = e.category || 'other';
    categoryTotals[cat] = (categoryTotals[cat] || 0) + (e.amount || 0);
  });

  let topCategory = null;
  let topAmount = 0;
  Object.entries(categoryTotals).forEach(([cat, amount]) => {
    if (amount > topAmount) {
      topAmount = amount;
      const catInfo = EXPENSE_CATEGORIES.find(c => c.value === cat);
      topCategory = {
        value: cat,
        label: catInfo?.label || cat,
        amount: topAmount,
        icon: getCategoryIcon(cat),
      };
    }
  });

  return { totalSpent, averageMonthly, topCategory, expenseCount: filteredExpenses.length };
};

/**
 * Category breakdown for pie/bar chart.
 * Returns sorted descending by amount.
 */
export const computeCategoryBreakdown = (filteredExpenses) => {
  const totals = {};
  let grandTotal = 0;

  filteredExpenses.forEach(e => {
    const cat = e.category || 'other';
    totals[cat] = (totals[cat] || 0) + (e.amount || 0);
    grandTotal += (e.amount || 0);
  });

  return Object.entries(totals)
    .map(([category, amount]) => {
      const catInfo = EXPENSE_CATEGORIES.find(c => c.value === category);
      return {
        category,
        label: catInfo?.label || category,
        amount,
        percentage: grandTotal > 0 ? Math.round((amount / grandTotal) * 100) : 0,
        icon: getCategoryIcon(category),
      };
    })
    .sort((a, b) => b.amount - a.amount);
};

/**
 * Monthly spending totals for trend chart.
 * Fills zero-amount months so the line has no gaps.
 */
export const computeMonthlyTrend = (expenses, startDate, endDate) => {
  // Build a map of month -> total from ALL expenses in range
  const monthTotals = {};
  expenses.forEach(e => {
    const date = toJsDate(e.date);
    if (!date || date < startDate || date > endDate) return;
    const key = format(date, 'yyyy-MM');
    monthTotals[key] = (monthTotals[key] || 0) + (e.amount || 0);
  });

  // Generate every month in the range
  const months = eachMonthOfInterval({ start: startDate, end: endDate });

  return months.map(m => {
    const key = format(m, 'yyyy-MM');
    return {
      month: key,
      monthLabel: format(m, 'MMM yyyy'),
      shortLabel: format(m, 'MMM'),
      total: monthTotals[key] || 0,
    };
  });
};

/**
 * Per-child spending breakdown.
 * Multi-child expenses split evenly. Expenses with no childIds go to "Unassigned".
 */
export const computePerChildBreakdown = (filteredExpenses, childrenMap) => {
  const childTotals = {};

  filteredExpenses.forEach(e => {
    const rawIds = e.childIds?.length ? e.childIds : e.childId ? [e.childId] : [];
    const ids = rawIds.length ? rawIds : ['__unassigned__'];
    const perChild = (e.amount || 0) / ids.length;

    ids.forEach(id => {
      if (!childTotals[id]) {
        const child = childrenMap?.[id];
        childTotals[id] = {
          childId: id,
          childName: id === '__unassigned__' ? 'Unassigned' : (child?.name || child?.firstName || 'Unknown'),
          childColor: id === '__unassigned__' ? '#78716c' : (child?.color || '#78716c'),
          total: 0,
          categories: {},
        };
      }
      childTotals[id].total += perChild;

      const cat = e.category || 'other';
      childTotals[id].categories[cat] = (childTotals[id].categories[cat] || 0) + perChild;
    });
  });

  // Convert categories map to sorted array
  return Object.values(childTotals)
    .map(child => ({
      ...child,
      categories: Object.entries(child.categories)
        .map(([cat, amount]) => {
          const catInfo = EXPENSE_CATEGORIES.find(c => c.value === cat);
          return { category: cat, label: catInfo?.label || cat, amount, icon: getCategoryIcon(cat) };
        })
        .sort((a, b) => b.amount - a.amount),
    }))
    .sort((a, b) => b.total - a.total);
};

/**
 * Generate CSV string from expenses.
 */
export const exportExpensesToCSV = (filteredExpenses, childrenMap, parentProfiles) => {
  const headers = ['Date', 'Description', 'Category', 'Amount', 'Paid By', 'Children', 'Status', 'Split %', 'Owed Amount', 'Note'];

  const escapeCSV = (value) => {
    const str = String(value ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = filteredExpenses.map(e => {
    const date = toJsDate(e.date);
    const dateStr = date ? format(date, 'yyyy-MM-dd') : '';
    const catInfo = EXPENSE_CATEGORIES.find(c => c.value === e.category);
    const categoryLabel = catInfo?.label || e.category || '';
    const paidByName = parentProfiles?.[e.paidBy]?.displayName || e.paidBy || '';
    const expChildIds = e.childIds?.length ? e.childIds : e.childId ? [e.childId] : [];
    const childNames = expChildIds
      .map(id => childrenMap?.[id]?.name || childrenMap?.[id]?.firstName || 'Unknown')
      .join(', ');

    return [
      dateStr,
      e.description || '',
      categoryLabel,
      (e.amount || 0).toFixed(2),
      paidByName,
      childNames,
      e.status || '',
      e.splitRatio ?? '',
      (e.owedAmount || 0).toFixed(2),
      e.note || '',
    ].map(escapeCSV).join(',');
  });

  return [headers.join(','), ...rows].join('\n');
};

/**
 * Trigger browser download of a CSV file.
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Weekly spending totals for trend chart.
 * Used for shorter time horizons (this month, last 3 months).
 */
export const computeWeeklyTrend = (expenses, startDate, endDate) => {
  const weekTotals = {};
  expenses.forEach(e => {
    const date = toJsDate(e.date);
    if (!date || date < startDate || date > endDate) return;
    const weekStart = startOfWeek(date, { weekStartsOn: 0 });
    const key = format(weekStart, 'yyyy-MM-dd');
    weekTotals[key] = (weekTotals[key] || 0) + (e.amount || 0);
  });

  const weeks = eachWeekOfInterval({ start: startDate, end: endDate }, { weekStartsOn: 0 });

  return weeks.map(w => {
    const key = format(w, 'yyyy-MM-dd');
    const weekEnd = addDays(w, 6);
    return {
      week: key,
      weekLabel: `${format(w, 'MMM d')} – ${format(weekEnd, 'd')}`,
      shortLabel: format(w, 'MMM d'),
      total: weekTotals[key] || 0,
    };
  });
};

/**
 * Pick weekly or monthly trend based on preset.
 */
export const computeTrendData = (expenses, startDate, endDate, preset) => {
  if (preset === 'this_month' || preset === 'last_3') {
    return computeWeeklyTrend(expenses, startDate, endDate);
  }
  return computeMonthlyTrend(expenses, startDate, endDate);
};

/**
 * Date range preset options for the UI.
 */
export const DATE_RANGE_PRESETS = [
  { key: 'this_month', label: 'This Month' },
  { key: 'last_3', label: 'Last 3 Months' },
  { key: 'last_6', label: 'Last 6 Months' },
  { key: 'this_year', label: 'This Year' },
];

/**
 * Brand-aligned colors for expense categories.
 */
export const CATEGORY_COLORS = {
  activities: '#1B6B4A',
  medical: '#F2886D',
  school: '#3BA676',
  clothing: '#F5C842',
  food: '#8b5cf6',
  childcare: '#06b6d4',
  transportation: '#6366f1',
  entertainment: '#ec4899',
  other: '#78716c',
};
